import {Fragment, useMemo, useRef, useState} from "react";
import {Link as RouterLink} from "react-router-dom";
import {
  createListCollection,
  Table,
  Box,
  Text,
  Select,
  Button,
  Link,
  Portal,
} from "@chakra-ui/react";
import {CSVLink} from "react-csv";
import {useCalculator, useGetTotalConfig} from "../context/calculatorContext/context";
import {useHandleFrequency} from "../hooks/useHandleFrequency";
import {useHandleOs} from "../hooks/useHandleOs";
import {useHandlePlan} from "../hooks/useHandlePlan";
import {calculatePlanPrice} from "../utils/calculatePlanPrice";

const selectOptions = createListCollection({
  items: [
    {label: "CSV", value: "CSV"},
    {label: "PDF", value: "PDF"},
    {label: "JSON", value: "JSON"},
  ],
});

const EstimateTable = () => {
  const [selectedOption, setSelectedOption] = useState(["Export"]);
  const csvLinkRef = useRef();

  const {requestId, selectedData} = useCalculator();
  const {ramInGB, showInGB, totalCPU, totalRAM} = useGetTotalConfig();
  const {planType, selectedPlan} = useHandlePlan();
  const {selectedOS} = useHandleOs(planType);
  const {selectedFrequency} = useHandleFrequency(selectedOS);

  const totalCost = useMemo(() => {
    const total = Math.round(
      calculatePlanPrice(selectedPlan.prices, selectedFrequency) +
        calculatePlanPrice(selectedOS?.prices, selectedFrequency)
    );
    return total;
  }, [selectedPlan, selectedOS, selectedFrequency]);

  const handleSelectChange = (event) => {
    let val = event.value;
    setSelectedOption(val);

    if (val[0] === "JSON") downloadJSON(selectedData);
    else if (val[0] === "PDF") generatePDF(selectedData);
    else if (val[0] === "CSV") csvLinkRef.current.link.click();
  };

  return (
    <Fragment>
      {selectedData && selectedData.length > 0 ? (
        <Fragment>
          <Box
            width={"100%"}
            margin={"0 0 1rem 0"}
            fontSize={"sm"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"flex-start"}
          >
            <Box margin={"0 1.75rem 0 0"} id="summary">
              <Box display={"flex"} gap={2} data-cy="estimate-date">
                <Text color={"#111b27"} fontWeight={"500"}>
                  Estimate Date:
                </Text>
                <Text>
                  {new Date().toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </Text>
              </Box>
              <Box display={"flex"} gap={2}>
                <Text color={"#111b27"} fontWeight={"500"} data-cy="service-item">
                  Service Item:
                </Text>
                <Text>
                  {selectedFrequency
                    ? `${selectedFrequency.period} ${selectedFrequency.period_unit}`
                    : "N/A"}
                </Text>
              </Box>
              <Box display={"flex"} gap={2}>
                <Text color={"#111b27"} fontWeight={"500"} data-cy="total-cpu">
                  Total CPU:
                </Text>
                <Text>{totalCPU}</Text>
              </Box>
              <Box display={"flex"} gap={2} data-cy="total-ram">
                <Text color={"#111b27"} fontWeight={"500"}>
                  Total RAM:
                </Text>
                <Text>{showInGB ? `${ramInGB} GB` : `${totalRAM} MB`}</Text>
              </Box>
              <Box display={"flex"} gap={2} data-cy="request-id">
                <Text color={"#111b27"} fontWeight={"500"}>
                  Request Id:
                </Text>
                <Text>{requestId}</Text>
              </Box>
              {planType !== "Custom" && (
                <Fragment>
                  {totalCost ? (
                    <Box display={"flex"} gap={2}>
                      <Text color={"#111b27"} fontSize={"14px"} fontWeight={"500"}>
                        Total Cost:
                      </Text>
                      <Text>{`$ ${totalCost} USD`}</Text>
                    </Box>
                  ) : null}
                </Fragment>
              )}
              <Box display={"flex"} gap={2}>
                <Text>&nbsp;</Text>
              </Box>
            </Box>
          </Box>
          <Box
            width={"100%"}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
            margin={"-1.5rem 0 0 0"}
          >
            <Box />
            <Box display={"flex"} gap={2}>
              <Link as={RouterLink} to="/vendor/all-vendor">
                <button
                  style={{
                    padding: "5px 15px",
                    color: "white",
                    background: "hsl(197, 65%, 29%)",
                    border: "1px solid hsl(197, 65%, 29%)",
                    fontSize: "14px",
                    fontWeight: "500",
                    transition: "0.15s",
                    position: "relative",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "hsl(197, 65%, 35%)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "hsl(197, 65%, 29%)";
                  }}
                >
                  Add Service
                </button>
              </Link>
              <Box display={"flex"} gap={1}>
                {selectedData.length > 0 && (
                  <Select.Root
                    collection={selectOptions}
                    data-cy="export-drop-down"
                    onValueChange={handleSelectChange}
                    size={"sm"}
                    value={selectedOption}
                    width={"200px"}
                  >
                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder="Export" />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>
                    <Portal>
                      <Select.Positioner>
                        <Select.Content>
                          {selectOptions.items.map((option) => (
                            <Select.Item item={option} key={option.value}>
                              {option.label}
                              <Select.ItemIndicator />
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Positioner>
                    </Portal>
                  </Select.Root>
                )}
                <CSVLink
                  ref={csvLinkRef}
                  filename="cml-estimate.csv"
                  data={selectedData.map((item, idx) => {
                    return {
                      Id: ++idx,
                      Name: item.name,
                      CPU: item.cpu,
                      Ram: item.ram >= 1024 ? item.ram / 1024 + " GB" : item.ram + " MB",
                      Vendor: item.vendor,
                      Quantity: item.quantity,
                      "Total CPU": item.quantity * item.cpu,
                      "Total RAM":
                        item.ram * item.quantity >= 1024
                          ? (item.ram * item.quantity) / 1024 + " GB"
                          : item.ram * item.quantity + " MB",
                    };
                  })}
                >
                  <Button
                    size={"sm"}
                    display={"none"}
                    colorScheme="gray"
                    variant="outline"
                    fontWeight={"400"}
                    padding={"0 25px"}
                  >
                    Export to CSV
                  </Button>
                </CSVLink>
              </Box>
            </Box>
          </Box>
          <Box mt={4}>
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row
                  textTransform={"capitalize"}
                  fontWeight={"500"}
                  background={"#262626"}
                  color={"#fff"}
                >
                  <Table.ColumnHeader data-cy="table-header">#</Table.ColumnHeader>
                  <Table.ColumnHeader data-cy="table-header">Name</Table.ColumnHeader>
                  <Table.ColumnHeader data-cy="table-header">CPU</Table.ColumnHeader>
                  <Table.ColumnHeader data-cy="table-header">Ram</Table.ColumnHeader>
                  <Table.ColumnHeader data-cy="table-header">Vendor</Table.ColumnHeader>
                  <Table.ColumnHeader data-cy="table-header">Quantity</Table.ColumnHeader>
                  <Table.ColumnHeader data-cy="table-header">
                    Total CPU
                  </Table.ColumnHeader>
                  <Table.ColumnHeader data-cy="table-header">
                    Total RAM
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {selectedData?.map((item, idx) => {
                  return (
                    <Table.Row key={idx}>
                      <Table.Cell width={"min-content"}>{++idx}</Table.Cell>
                      <Table.Cell>{item?.name}</Table.Cell>
                      <Table.Cell>{item?.cpu}</Table.Cell>
                      <Table.Cell>
                        {item?.ram >= 1024 ? item?.ram / 1024 + " GB" : item?.ram + " MB"}
                      </Table.Cell>
                      <Table.Cell>{item?.vendor}</Table.Cell>
                      <Table.Cell>{item?.quantity}</Table.Cell>
                      <Table.Cell>{item?.cpu * item?.quantity}</Table.Cell>
                      <Table.Cell>
                        {item?.ram * item?.quantity >= 1024
                          ? (item?.ram * item?.quantity) / 1024 + " GB"
                          : item?.ram * item?.quantity + " MB"}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Root>
          </Box>
          <Box
            margin={"1rem 0 0.5rem 0"}
            color={"#6b7280"}
            display={"flex"}
            gap={"0.25reM"}
            data-cy="inquiry-submission"
          >
            <Text color={"red"} display={"inline-block"}>
              *
            </Text>
            Please forward your downloaded request to{" "}
            <a
              href="mailto:sales@cloudmylab.com"
              style={{textDecoration: "underline", textUnderlineOffset: "3px"}}
            >
              sales@cloudmylab.com
            </a>
            , and our team will be pleased to assist you with your inquiry.
          </Box>
        </Fragment>
      ) : (
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"center"}
          minH={"100px"}
          border={"1px dashed #6b7280"}
          color={"#6b7280"}
          margin={"1rem 0 0 0"}
          data-cy="empty-state-message"
        >
          Please Select Some Vendor
        </Box>
      )}
    </Fragment>
  );
};

export default EstimateTable;

const generatePDF = async (selectedData) => {
  // Dynamically import heavy deps only when exporting PDF
  const [{default: jsPDFConstructor}, {default: autoTable}, {default: html2canvas}] =
    await Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
      import("html2canvas"),
    ]);
  let data = selectedData.map((item, idx) => {
    return {
      Id: ++idx,
      Name: item.name,
      CPU: item.cpu,
      Ram: item.ram >= 1024 ? item.ram / 1024 + " GB" : item.ram + " MB",
      Vendor: item.vendor,
      Quantity: item.quantity,
      "Total CPU": item.quantity * item.cpu,
      "Total RAM":
        item.ram * item.quantity >= 1024
          ? (item.ram * item.quantity) / 1024 + " GB"
          : item.ram * item.quantity + " MB",
    };
  });

  const doc = new jsPDFConstructor();

  const headers = Object.keys(data[0]);
  const body = data.map((obj) => Object.values(obj));
  doc.setPage(1);

  // html content 1 of estimate image
  let htmlContent = document.getElementById("company-info");
  let canvas = await html2canvas(htmlContent);
  let imgData = canvas.toDataURL("image/png");
  doc.addImage(imgData, "PNG", 15, 5, 150, 60);

  // html content 2
  htmlContent = document.getElementById("summary");
  canvas = await html2canvas(htmlContent);
  imgData = canvas.toDataURL("image/png");
  doc.addImage(imgData, "PNG", 15, 70, 40, 27);

  autoTable(doc, {
    head: [headers],
    body: body,
    startY: 100,
    headStyles: {
      fillColor: [0, 0, 0],
      textColor: [255, 255, 255],
    },
  });

  doc.save("cml-estimate.pdf");
};

const downloadJSON = (selectedData) => {
  let data = selectedData.map((item, idx) => {
    return {
      Id: ++idx,
      Name: item.name,
      CPU: item.cpu,
      Ram: item.ram >= 1024 ? item.ram / 1024 + " GB" : item.ram + " MB",
      Vendor: item.vendor,
      Quantity: item.quantity,
      "Total CPU": item.quantity * item.cpu,
      "Total RAM":
        item.ram * item.quantity >= 1024
          ? (item.ram * item.quantity) / 1024 + " GB"
          : item.ram * item.quantity + " MB",
    };
  });

  const jsonData = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonData], {type: "application/json"});
  const a = document.createElement("a");

  a.href = URL.createObjectURL(blob);
  a.download = "cml-estimate.json";

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};
